import { useEffect, useState } from "react";
import { PhoneIncoming, PhoneMissed } from "lucide-react";
import { adminApi, api } from "@/lib/sleek";

export default function CallsManager({ token, onChange }) {
  const [calls, setCalls] = useState([]);
  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      adminApi(token).get("/admin/calls"),
      api.get("/call-tracking-number"),
    ]).then(([c, t]) => {
      setCalls(c.data);
      setConfigured(t.data.configured);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id) => {
    await adminApi(token).patch(`/admin/calls/${id}/resolve`);
    load();
    onChange?.();
  };

  if (loading) return <div className="mt-6 text-sm text-zinc-500" data-testid="calls-loading">Loading calls…</div>;

  if (!configured && calls.length === 0) {
    return (
      <div className="mt-6 bg-white border border-zinc-200 p-10 text-center" data-testid="calls-not-configured">
        <div className="font-display text-xl font-light">Call tracking isn't set up yet.</div>
        <p className="text-zinc-500 text-sm mt-2 max-w-md mx-auto">
          Once a Twilio tracking number is purchased and TWILIO_NUMBER is set in the backend .env,
          calls to your landing page's tracked number will show up here with caller number, status, and duration —
          and anyone whose call goes unanswered will automatically get a text-back so you don't lose the lead.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {!configured && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm p-4 mb-4" data-testid="calls-not-configured-banner">
          Call tracking number isn't set up yet — showing call history captured so far. New calls won't come in until TWILIO_NUMBER is set.
        </div>
      )}
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="calls-table-wrapper">
        <table className="w-full text-sm" data-testid="admin-calls-table">
        <thead className="bg-zinc-50 text-left text-[11px] tracking-[0.2em] uppercase text-zinc-500">
          <tr><th className="p-4">Date</th><th className="p-4">Caller</th><th className="p-4">Status</th><th className="p-4">Duration</th><th className="p-4">Text-back</th><th className="p-4"></th></tr>
        </thead>
        <tbody>
          {calls.map((c) => (
            <tr key={c.id} className={`border-t border-zinc-100 ${c.resolved ? "opacity-50" : ""}`} data-testid={`admin-call-${c.id}`}>
              <td className="p-4 text-zinc-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
              <td className="p-4 font-medium">{c.from_number || "Unknown"}</td>
              <td className="p-4">
                {c.answered ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700"><PhoneIncoming size={13} /> Answered</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-zinc-400"><PhoneMissed size={13} /> {c.status === "received" ? "In progress" : "Missed"}</span>
                )}
              </td>
              <td className="p-4">{c.duration_seconds ? `${c.duration_seconds}s` : "—"}</td>
              <td className="p-4">
                {!c.answered && c.sms_sent && <span className="text-emerald-700 text-xs">Sent</span>}
                {!c.answered && c.sms_sent === false && <span className="text-red-500 text-xs">Failed</span>}
                {(c.answered || c.sms_sent === undefined) && <span className="text-zinc-300 text-xs">—</span>}
              </td>
              <td className="p-4 text-right whitespace-nowrap">
                {!c.answered && (c.resolved
                  ? <span className="text-xs text-zinc-400">Resolved</span>
                  : <button onClick={() => resolve(c.id)} className="text-xs text-zinc-600 hover:text-zinc-950 underline" data-testid={`admin-call-resolve-${c.id}`}>Mark resolved</button>
                )}
              </td>
            </tr>
          ))}
          {calls.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-zinc-500">No calls yet.</td></tr>}
        </tbody>
        </table>
      </div>
    </div>
  );
}
